import * as z from "zod";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../providers/form";
import InputField from "widgets/shared/inputs/inputField";
import { useTranslation } from "react-i18next";
import GenericButton from "widgets/controls/buttons/generic";
import { useEffect } from "react";
import MaskInput from "widgets/shared/inputs/maskInput";
// Schema
const PlayTokenSchema = z.object({
  meterNumber: z.string().min(1, { message: "Meter number is required" }),
  token: z.string().min(1, { message: "Token is required" }),
});

export type PlayTokenFormType = z.infer<typeof PlayTokenSchema>;

// Props
type PlayTokenFormProps = {
  submit: (data: PlayTokenFormType) => void;
  onClose: () => void;
  defaultValues?: PlayTokenFormType;
  values?: PlayTokenFormType;
  loading?: boolean;
  disabled?: boolean;
};

const PlayTokenForm = ({
  submit,
  onClose,
  defaultValues,
  values,
  loading,
  disabled,
}: PlayTokenFormProps) => {
  const form = useForm<PlayTokenFormType>({
    resolver: zodResolver(PlayTokenSchema),
    values,
  });

  const { t } = useTranslation();
  const { errors, isDirty } = form.formState;

  const clearErrors = () => {
    form.clearErrors();
  };

  // Automatically submit the form if valid
  const onSubmit = async (formData: PlayTokenFormType) => {
    try {
      await submit(formData);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  useEffect(() => {
    if (disabled && isDirty) {
      form.reset(values || defaultValues);
    }
  }, [disabled, isDirty, form, values, defaultValues]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className=" space-y-2">
        <div className="grid grid-cols-1 gap-2">
          <FormField
            control={form.control}
            name="meterNumber"
            rules={{ required: true }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("form.input.meter.number")}
                </FormLabel>
                <FormControl>
                  <InputField
                    disabled={true}
                    placeholder={t("form.placeholder.meter.number")}
                    id="meterNumber-input"
                    type="text"
                    state={errors.meterNumber ? "error" : undefined}
                    onFocus={clearErrors}
                    variant="solid"
                    color="secondary"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="token"
            rules={{ required: true }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("form.input.token")}
                </FormLabel>
                <FormControl>
                  <MaskInput
                    disabled={loading}
                    placeholder={t("form.placeholder.token")}
                    id="token-input"
                    type="text"
                    state={errors.token ? "error" : undefined}
                    onFocus={clearErrors}
                    color="secondary"
                    variant="solid"
                    mask="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX"
                    replacement={{
                      "X": /[0-9]/
                    }}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
       
        <div className="flex justify-end py-2">
          <div className="flex gap-2">
            <GenericButton
              onClick={onClose}
              text={t("button.action.cancel")}
              size="md"
              variant="ghost"
              color="secondary"
            />

            <GenericButton
              type="submit"
              text={t("button.action.play")}
              loading={loading || false}
              size="md"
            />
          </div>
        </div>
      </form>
    </Form>
  );
};

export default PlayTokenForm;
